import { type TableProps, Tag } from 'antd';
import { type JSX } from 'react';

import { getTranslatedMessage } from '@client/utils/i18n.util';
import { columnsCache } from '@client/utils/table/column.util';
import {
  columnFilter,
  type TColumnFilter,
} from '@client/utils/table/filter.util';
import { columnSorter } from '@client/utils/table/sorter.util';
import { indexColumn } from '@client/utils/table/table.util';
import { tsToLocaleDateTime } from '@common/utils';

import type { TFilters, TSorts } from '@client/hooks/useTable';
import type { ELocales, EPricingKind, IPricingRule } from '@common/types';

type TArgs = {
  locale: ELocales;
  sortedInfo: TSorts;
  filteredInfo: TFilters;
  entities: IPricingRule[];
};

const KIND_COLOR_MAP: Record<EPricingKind, string> = {
  FIXED: 'purple',
  PER_MINUTE: 'cyan',
  PER_REQUEST: 'geekblue',
};

export const columnsMetadata = (
  props: TArgs,
  filterIn: string[] = [],
): TableProps<IPricingRule>['columns'] => {
  const { locale, sortedInfo, entities, filteredInfo } = props;

  const { cached, updateCached } = columnsCache<IPricingRule>({
    sortedInfo,
    filteredInfo,
    entities,
    locale,
    filterIn,
  });

  if (cached) {
    return cached;
  }

  const t = getTranslatedMessage;

  const columns = [
    indexColumn,
    {
      title: t('form.entitlements.operation'),
      dataIndex: 'operation',
      key: 'operation',
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'operation',
      ) as TColumnFilter<IPricingRule>),
      ...columnSorter(sortedInfo, 'operation', 'operation', 0),
      render: (operation: string): JSX.Element => <span>{operation}</span>,
    },
    {
      title: t('form.entitlements.kind'),
      dataIndex: 'kind',
      key: 'kind',
      width: 140,
      align: 'center' as const,
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'kind',
      ) as TColumnFilter<IPricingRule>),
      ...columnSorter(sortedInfo, 'kind', 'kind', 1),
      render: (kind: EPricingKind): JSX.Element => (
        <Tag color={KIND_COLOR_MAP[kind] ?? 'default'}>{kind}</Tag>
      ),
    },
    {
      title: t('form.entitlements.costPerUnit'),
      dataIndex: 'costPerUnit',
      key: 'costPerUnit',
      width: 130,
      align: 'right' as const,
      concealable: true,
      ...columnSorter(sortedInfo, 'costPerUnit', 'costPerUnit', 2),
      render: (costPerUnit: number): JSX.Element => <span>{costPerUnit}</span>,
    },
    {
      title: t('form.entitlements.version'),
      dataIndex: 'version',
      key: 'version',
      width: 90,
      align: 'center' as const,
      concealable: true,
      ...columnSorter(sortedInfo, 'version', 'version', 3),
      render: (version: number): JSX.Element => <span>{version}</span>,
    },
    {
      title: t('form.entitlements.isActive'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 90,
      align: 'center' as const,
      concealable: true,
      render: (isActive: boolean): JSX.Element => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: t('form.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      align: 'center' as const,
      concealable: true,
      ...columnSorter(sortedInfo, 'createdAt', 'createdAt', 4),
      render: (createdAt: Date): JSX.Element => (
        <div>{tsToLocaleDateTime(createdAt)}</div>
      ),
    },
  ];

  const result = filterIn.length
    ? columns.filter(
        (column) =>
          filterIn.includes(column?.dataIndex as string) ||
          column.dataIndex === 'idx',
      )
    : columns;

  return updateCached(result);
};
